import dayjs from 'dayjs';
import validator from 'validator';

// 检查是否url
export const checkIsUrl = (url: string, requireProtocol = false) => {
  if (!url) return false;
  return validator.isURL(url, { require_protocol: requireProtocol });
};

// Function to generate a random number between min and max
export const getRandomNumber = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// 解析提供的字符串日期，然后计算它们之间的天数。基于天数，决定返回哪种日期数组。
export const generateDateRange = (start: string, end: string): string[] => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  // Calculate the difference in days
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysDifference = Math.round((endDate.getTime() - startDate.getTime()) / msPerDay);

  const results: string[] = [];

  // Calculate the difference in months
  const monthsDifference =
    (endDate.getFullYear() - startDate.getFullYear()) * 12 + endDate.getMonth() - startDate.getMonth();

  // When the difference is more than a month and also more than or equal to six months
  if (daysDifference > 30 && monthsDifference >= 6) {
    let currentMonth = startDate;
    while (currentMonth <= endDate) {
      const month = String(currentMonth.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed in JS
      results.push(`${currentMonth.getFullYear()}-${month}`);
      currentMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
    }
  } else {
    // Generate dates by day
    let currentDay = new Date(startDate);
    while (currentDay <= endDate) {
      const month = String(currentDay.getMonth() + 1).padStart(2, '0');
      const day = String(currentDay.getDate()).padStart(2, '0');
      results.push(`${currentDay.getFullYear()}-${month}-${day}`);
      currentDay = new Date(currentDay.getTime() + msPerDay); // Move to the next day
    }
  }

  return results;
};

/**
 * 检查两个时间是否在同一天。
 * 考虑到了时区问题。
 * @param approximateTime 用户在日期选择组件上选择而来。格式为"YYYY-MM"|"YYYY-MM-DD"
 * @param isoTime 接口返回数据中的时间，带时区信息。
 * @returns
 */
export const isSameDay = (approximateTime: string, isoTime: string): boolean => {
  const isMonthFormat = approximateTime.length === 7;

  if (isMonthFormat) {
    const [year, month] = approximateTime.split('-').map(Number);
    const startOfMonth = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`).startOf('day');
    const endOfMonth = startOfMonth.endOf('month');
    const isoDate = dayjs(isoTime);

    return (
      isoDate.isSame(startOfMonth, 'day') ||
      isoDate.isSame(endOfMonth, 'day') ||
      (isoDate.isAfter(startOfMonth, 'day') && isoDate.isBefore(endOfMonth, 'day'))
    );
  } else {
    return dayjs(isoTime).isSame(approximateTime, 'day');
  }
};

// 计算出百分比
export const calculatePercentage = (num1: number, num2: number): number => {
  let base: number;
  let fraction: number;

  // Determine the base and fraction based on your requirement
  // Assuming base is the larger number
  if (num1 > num2) {
    base = num1;
    fraction = num2;
  } else {
    base = num2;
    fraction = num1;
  }

  const percentage = (fraction / base) * 100;
  return Math.round(percentage); // Round to nearest integer
};

//传入零时区 ISO 8601 格式时间返回相加浏览器时区后的时间
export const adjustToBrowserTimezone = (isoTime: string): string => {
  const utcDate = new Date(isoTime);

  // Get the timezone offset in minutes
  const timezoneOffset = utcDate.getTimezoneOffset();

  // Adjust the date based on the timezone offset
  utcDate.setMinutes(utcDate.getMinutes() - timezoneOffset);

  return utcDate.toISOString();
};

// 处理API错误并返回国际化错误信息
export const handleApiError = (
  error: { name?: string; message?: string },
  t: (key: string) => string,
  fallbackMessage?: string
) => {
  // 如果有错误名称，尝试获取对应的翻译
  if (error.name) {
    const translationKey = `adminConsole.tip.${error.name}`;
    const translatedMessage = t(translationKey);

    // 如果翻译成功（返回的不是翻译键本身），返回翻译后的消息
    if (translatedMessage && translatedMessage !== translationKey) {
      return translatedMessage;
    }
  }

  // 如果没有找到对应翻译，返回后备消息
  // 接口返回的原始错误可能有中文
  return fallbackMessage;
};

/**
 * 从用户 ID 中提取 OGS 用户 ID
 * user_id 格式可能是 "auth0|647ef1abed1539be4d1a6aba" 或纯数字ID
 * 如果包含竖线，取竖线后面的内容作为 OGS 用户 ID
 * @param userId 原始用户 ID（可能来自 /users/me 接口）
 * @returns OGS 用户 ID
 */
export const extractOgsUserId = (userId: string | undefined | null): string => {
  if (!userId) return '';

  // 如果包含竖线，取竖线后面的内容
  if (userId.includes('|')) {
    return userId.split('|')[1] || '';
  }

  // 没有竖线，直接返回原值
  return userId;
};

// 复制到粘贴板
// Mantine CopyButton 是用navigator.clipboard实现的，只能用在https下(则本地部署不能用)
export const copyToClipboard = (text: string) => {
  const textBox = document.createElement('textarea');
  textBox.value = text;
  textBox.style.position = 'fixed';
  textBox.style.opacity = '0';
  document.body.appendChild(textBox);

  textBox.select();
  textBox.setSelectionRange(0, 99999); // 对于移动设备

  // 尝试复制文本
  try {
    const successful = document.execCommand('copy');
    document.body.removeChild(textBox);
    return successful;
  } catch (err) {
    document.body.removeChild(textBox);
    return false;
  }
};

/**
 * 获取用户名的首字母缩写（用于头像显示）
 * @param name 用户名
 * @returns 首字母大写
 */
export const getNameAbbr = (name: string) => {
  if (!name) return '';
  return name[0].toLocaleUpperCase();
};
